<template>
  <section class="section">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{ App.OfficialModelsScreen.DOCUMENT_MODELS }}
      </h1>
      <div class="header-buttons column">
        <b-button
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          class="has-text-weight-semibold spacing-mr-5"
          iconLeft="plus"
          tag="router-link"
          :expanded="isMobile"
          :to="newModel()"
          >{{ App.OfficialModelsScreen.NEW_MODEL }}</b-button
        >
      </div>
    </div>

    <GenericPanelSearch
      :filterList="filterList"
      :requestBaseData="requestBaseData"
      :searchRequest="listDocumentEdoweb"
      :filterOptions="getFilterOptions"
      :tabPersistName="tabPersistName"
      @filters="setFilter"
    />

    <div class="level-left has-text-left spacing-mb-10">
      <span class="ml-10">
        {{ getPaginationDetails }}
      </span>
    </div>

    <section class="spacing-mb-20">
      <b-table
        v-if="observerPage"
        class="custom-table-administration custom-6-columns-adm"
        :data="modelList"
        :sortIcon="sortIcon"
        :sortIconSize="sortIconSize"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        :defaultSort="sortOrder"
        :defaultSortDirection="defaultSortOrder"
        backendSorting
        type="is-black"
        hoverable
        @sort="onSort"
      >
        <template>
          <b-table-column v-slot="props" :label="General.CODE">
            <div class="has-text-color-8">{{ props.row.code }}</div>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.NAME"
            field="name"
            :sortable="!isMobile"
          >
            <div class="has-text-color-8 has-text-left">
              {{ `${props.row.name}` }}
            </div>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.TYPE"
            field="document_type"
            :sortable="!isMobile"
          >
            <div class="has-text-color-8">
              {{ props.row.document_type }}
            </div>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.COVERAGE"
            field="document_coverage"
            :sortable="!isMobile"
          >
            <div
              v-if="props.row.document_coverage === documentCoverage.NATURE"
              class="has-text-color-8"
            >
              {{ props.row.nature }}
            </div>
            <div
              v-else-if="
                props.row.document_coverage === documentCoverage.SPECIFIC
              "
              class="has-text-color-8"
            >
              <b-tooltip
                :label="coverageSpecific(props.row.parts)"
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
                :active="props.row.parts.length > 1"
                multilined
              >
                {{ props.row.document_coverage }}
                {{ props.row.parts[0].abbreviation }}
                {{ props.row.parts.length > 1 ? ` e outros` : `` }}
              </b-tooltip>
            </div>
            <div v-else class="has-text-color-8">
              {{ props.row.document_coverage }}
            </div>
          </b-table-column>
          <b-table-column v-slot="props" :label="General.ACTIVE_MASC">
            <b-switch
              v-model="props.row.active"
              @input="value => activeModel(props.row.id, value)"
            />
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="isMobile ? '' : General.ACTIONS"
          >
            <ActionsGrouper :actions="actionList(props.row)" />
          </b-table-column>
        </template>
        <template #empty>
          <section class="section">
            <NoListMessage v-if="!isLoading" />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>
      </b-table>
    </section>

    <div
      class="is-flex is-align-items-center mb-40"
      :class="{ 'is-flex-direction-column-reverse': isMobile }"
    >
      <b-dropdown
        v-model="perPage"
        aria-role="list"
        position="is-top-right"
        :class="{ 'spacing-mb-40': isMobile }"
        :expanded="isMobile"
      >
        <template slot="trigger">
          <b-button class="custom-dropdown-button" :expanded="isMobile">
            {{ perPage }} {{ General.PER_PAGE.toLowerCase() }}

            <b-icon class="spacing-mx-5" icon="chevron-down" />
          </b-button>
        </template>
        <div class="spacing-p-5 has-text-centered">
          {{ General.RECORDS_FOR_PAGE }}
          <div class="list-page">
            <b-dropdown-item
              v-for="option in optionsPagination"
              :key="option"
              :value="option"
            >
              <div>
                {{ option }}
              </div>
            </b-dropdown-item>
          </div>
        </div>
      </b-dropdown>

      <div class="custom-pagination-container" :class="{ 'mb-10': isMobile }">
        <div class="column is-8">
          <b-pagination
            :total="getAllModels.count"
            :perPage="perPage"
            :current.sync="currentPage"
            rangeBefore="1"
            rangeAfter="1"
            order="is-centered"
            class="process-panel-pagination"
            rounded
            :iconPrev="prevIcon"
            :iconNext="nextIcon"
            :aria-next-label="General.NEXT_PAGE"
            :aria-previous-label="General.PREVIOUS_PAGE"
            :aria-page-label="General.PAGE"
            :aria-current-label="General.CURRENT_PAGE"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import debounce from "lodash/debounce";
import { mapActions, mapGetters } from "vuex";
import { App, General } from "@/utils/constants";
import NoListMessage from "@/components/NoListMessage";
import ModalWarning from "@/views/App/ModalWarning.vue";
import GenericPanelSearch from "@/components/GenericPanelSearch";
import {
  OFFICIAL_DOCUMENT_COVERAGE as documentCoverage,
  SELECT_STATUS_OPTIONS as statusOptions,
  TAB_PERSIST_NAME as PERSIST_NAME
} from "@/utils/enumerate";
import { formatPagination } from "@/utils/transformers";
import { setPersistSort, getPersistSort } from "@/plugins/indexDB";

export default {
  name: "DoeModels",

  components: {
    GenericPanelSearch,
    NoListMessage
  },

  data() {
    return {
      App,
      General,
      documentCoverage,
      isLoading: false,
      observerPage: true,
      perPage: 10,
      currentPage: 1,
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      defaultSortOrder: "desc",
      sortIcon: "menu-up",
      sortIconSize: "is-medium",
      sortField: JSON.stringify({ standard: "desc" }),
      sortOrder: ["standard", "desc"],
      modelSearch: "",
      typeSearch: {},
      filterList: {},
      ignoreOrderByDefault: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      optionsPagination: "getItensPage",
      getAllModels: "getEdowebDocumentAdm",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    fileTypes() {
      return this.getFileTypes.filter(type => type.layout_template);
    },

    getFilterOptions() {
      return {
        ORIGIN: {
          name: General.ORGAN,
          type: "ORIGIN",
          key: "origin"
        },
        TYPE_DOE: {
          name: General.TYPE,
          type: "TYPE_DOE",
          key: "type_doe"
        },
        COVERAGE: {
          name: General.COVERAGE,
          type: "COVERAGE",
          key: "document_coverage"
        },
        STATUS: {
          name: General.STATUS,
          type: "STATUS",
          key: "status",
          unique: true
        }
      };
    },

    modelList() {
      return this.getAllModels.list;
    },

    requestBaseData() {
      const configSearch = {
        page: this.currentPage,
        paginator_count: this.perPage,
        order_by: this.sortField,
        ignore_order_by_default: this.ignoreOrderByDefault
      };
      if (this.filterList) {
        configSearch.document_type = this.filterList.type_doe?.length
          ? JSON.stringify(this.filterList.type_doe?.map(item => item.name))
          : null;
        configSearch.part = this.filterList.origin?.length
          ? JSON.stringify(this.filterList.origin?.map(item => item.id))
          : null;
        configSearch.document_coverage = this.filterList.document_coverage
          ?.length
          ? JSON.stringify(
              this.filterList.document_coverage?.map(item => item.name)
            )
          : null;
        configSearch.status = this.filterList.status
          ? this.formatStatus
          : undefined;
      }
      return configSearch;
    },

    formatStatus() {
      return this.filterList.status?.displayText === statusOptions.ACTIVE;
    },

    getPaginationDetails() {
      return formatPagination(
        this.getAllModels,
        this.currentPage,
        this.perPage
      );
    },

    tabPersistName() {
      return `${PERSIST_NAME.OFFICIAL_MODELS}_${this.user.id}`;
    }
  },

  watch: {
    requestBaseData: {
      handler() {
        this.getEdowebList();
      }
    }
  },

  async beforeMount() {
    this.observerPage = false;
    const sortData = await getPersistSort({
      name: this.tabPersistName,
      sort: JSON.parse(this.sortField)
    });
    const sortString = JSON.stringify(sortData);
    if (sortString !== this.sortField) {
      this.sortOrder = Object.entries(sortData)[0];
      this.onSort(...this.sortOrder);
    }
    this.observerPage = true;
    await this.getEdowebList();
  },

  methods: {
    ...mapActions({
      deleteModelRequest: "deleteModelDocumentEdowebById",
      listDocumentEdoweb: "listModelDocumentEdowebAdm",
      activeModelEdoweb: "activeModelDocumentEdowebById"
    }),
    async activeModel(id, active) {
      this.isLoading = true;
      await this.activeModelEdoweb({ id, active });
      await this.getEdowebList();
      this.isLoading = false;
    },
    newModel() {
      return `doe-editor`;
    },

    onSort(field, order) {
      this.sortField = JSON.stringify({ [field]: order });
      setPersistSort({
        name: this.tabPersistName,
        sort: { [field]: order }
      });
      this.ignoreOrderByDefault = true;
    },

    goToModel(id = false) {
      return {
        path: "doe-editor",
        query: { id }
      };
    },

    callDialog(index) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.CONFIRM,
          message: `${this.General.MESSAGES.ARE_YOU_SURE_YOU_WANT_DELETE_MODEL}`,
          confirm: () => this.deleteModel(index)
        },
        buttons: [
          { label: General.YES, isConfirm: false, action: true },
          { label: General.NO, isConfirm: true, action: false }
        ],
        hasModalCard: true,
        trapFocus: true
      });
    },

    getEdowebList: debounce(async function() {
      this.isLoading = true;
      if (this.observerPage)
        await this.listDocumentEdoweb(this.requestBaseData);
      this.isLoading = false;
    }, 1000),

    async deleteModel(modelId) {
      this.isLoading = true;
      await this.deleteModelRequest(modelId);
      await this.getEdowebList();
      this.isLoading = false;
    },

    setFilter(filters) {
      this.currentPage = 1;
      this.filterList = filters;
    },

    coverageSpecific(partsList) {
      if (partsList.length > 1) {
        return partsList.map(part => part.abbreviation).join(", ");
      }
    },

    actionList(item) {
      return [
        {
          label: this.General.EDIT,
          icon: "square-edit-outline",
          link: this.goToModel(item.id)
        },
        {
          label: this.General.DELETE,
          icon: "delete",
          callback: () => this.callDialog(item.id)
        }
      ];
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";
.section {
  background-color: $color-1;
  padding: 20px 1.5rem 0 1.5rem;

  .header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .has-custom-text-size {
      font-size: 0.75rem;
      font-weight: 600;

      @media screen and (max-width: $tablet) {
        font-size: 1rem;
      }
    }
  }
}

.document-type-button {
  min-width: 200px;
}
</style>
